import React from 'react';
import { Dialog, Transition } from '@headlessui/react';

interface DatasetItem {
  id: string;
  label: string;
  visible: boolean;
  color: string;
}

interface DatasetVisibilityModalProps {
  isOpen: boolean;
  onClose: () => void;
  datasets: DatasetItem[];
  onToggleVisibility: (id: string) => void;
}

const DatasetVisibilityModal: React.FC<DatasetVisibilityModalProps> = ({
  isOpen,
  onClose,
  datasets,
  onToggleVisibility,
}) => {
  if (!isOpen) return null;

  // Function to check if all datasets are visible
  const areAllVisible = () => {
    return datasets.every(dataset => dataset.visible);
  };
  
  // Function to check if none of the datasets are visible
  const areNoneVisible = () => {
    return datasets.every(dataset => !dataset.visible);
  };

  // Function to toggle all datasets
  const toggleAll = (setTo: boolean) => {
    datasets.forEach(dataset => {
      if (dataset.visible !== setTo) {
        onToggleVisibility(dataset.id);
      }
    });
  };

  return (
    <Transition appear show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="relative z-[10050]" onClose={onClose}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 shadow-xl transition-all">
                <div className="flex flex-col gap-4">
                  <div className="flex justify-between items-center border-b pb-2">
                    <Dialog.Title className="text-lg font-semibold">
                      Visible Datasets
                    </Dialog.Title>
                    <button
                      onClick={onClose}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                  
                  {/* All/None buttons */}
                  <div className="flex justify-end space-x-2 mb-2">
                    <button 
                      onClick={() => toggleAll(true)}
                      className={`text-xs px-3 py-1 rounded-md ${
                        areAllVisible() 
                          ? 'bg-blue-100 text-blue-700 font-medium' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      All
                    </button>
                    <button 
                      onClick={() => toggleAll(false)}
                      className={`text-xs px-3 py-1 rounded-md ${
                        areNoneVisible() 
                          ? 'bg-blue-100 text-blue-700 font-medium' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      None
                    </button>
                  </div>
                  
                  <div className="flex flex-col gap-2 mt-2 max-h-96 overflow-y-auto">
                    {datasets.map((dataset) => (
                      <button
                        key={dataset.id}
                        onClick={() => onToggleVisibility(dataset.id)}
                        className={`flex items-center px-3 py-2 rounded-md transition-colors ${
                          dataset.visible
                            ? 'bg-blue-50 border border-blue-500 text-blue-700'
                            : 'bg-gray-50 border border-gray-300 text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <div
                          className="h-4 w-4 mr-3 rounded-full"
                          style={{ backgroundColor: dataset.visible ? dataset.color : '#e5e7eb' }}
                        />
                        <span className="flex-1 text-sm text-left">{dataset.label}</span>
                      </button>
                    ))}
                  </div>
                  
                  <div className="flex justify-end pt-4">
                    <button
                      onClick={onClose}
                      className="eai-btn-grn"
                    >
                      Done
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default DatasetVisibilityModal;
